import type { CSSProperties } from "react";

/**
 * textarea と重ねる表示レイヤーが共有する、文字位置に影響するスタイル契約。
 */
export type TextareaOverlayTextStyle = Pick<
  CSSProperties,
  | "fontFamily"
  | "fontSize"
  | "fontStyle"
  | "fontWeight"
  | "letterSpacing"
  | "lineHeight"
  | "paddingBottom"
  | "paddingLeft"
  | "paddingRight"
  | "paddingTop"
  | "textAlign"
  | "textIndent"
  | "textTransform"
  | "wordSpacing"
>;

/**
 * mount 済み textarea から、表示レイヤーとの文字位置同期に必要な計算済みスタイルを返す。
 *
 * @remarks
 * `null` や textarea 未取得時のfallbackは呼び出し元が担う。この関数は受け取った要素に
 * `window.getComputedStyle()` を適用し、位置計算に関係しないstyleは公開契約へ含めない。
 */
export function getTextareaOverlayTextStyle(
  textarea: HTMLTextAreaElement,
): TextareaOverlayTextStyle {
  const style = window.getComputedStyle(textarea);

  return {
    fontFamily: style.fontFamily,
    fontSize: style.fontSize,
    fontStyle: style.fontStyle,
    fontWeight: style.fontWeight,
    letterSpacing: style.letterSpacing,
    lineHeight: style.lineHeight,
    paddingBottom: style.paddingBottom,
    paddingLeft: style.paddingLeft,
    paddingRight: style.paddingRight,
    paddingTop: style.paddingTop,
    textAlign: style.textAlign as CSSProperties["textAlign"],
    textIndent: style.textIndent,
    textTransform: style.textTransform as CSSProperties["textTransform"],
    wordSpacing: style.wordSpacing,
  };
}

/**
 * InputGroup 内で textarea の左上位置を求める。
 *
 * absolute positioning の原点は offsetParent の padding box なので、DOMRect の
 * border box との差である `clientLeft` / `clientTop` を除外する。
 *
 * 原則は `data-slot=input-group` を基準にし、取得できない場合のみ
 * offsetParent へフォールバックする。
 */
export function getTextareaRelativeOffset(textarea: HTMLTextAreaElement): {
  left: number;
  top: number;
} {
  const inputGroup = textarea.closest('[data-slot="input-group"]');
  const textareaRect = textarea.getBoundingClientRect();

  if (inputGroup instanceof HTMLElement) {
    const inputGroupRect = inputGroup.getBoundingClientRect();
    return {
      left: textareaRect.left - inputGroupRect.left - inputGroup.clientLeft,
      top: textareaRect.top - inputGroupRect.top - inputGroup.clientTop,
    };
  }

  const offsetParent = textarea.offsetParent;
  if (offsetParent instanceof HTMLElement) {
    const parentRect = offsetParent.getBoundingClientRect();
    return {
      left: textareaRect.left - parentRect.left - offsetParent.clientLeft,
      top: textareaRect.top - parentRect.top - offsetParent.clientTop,
    };
  }

  return {
    left: textarea.offsetLeft,
    top: textarea.offsetTop,
  };
}
